import { supabase } from '../../utils/supabase';
import { aggregatePlayerStats } from './transformers';

export interface DataAuditResult {
  player_number: number;
  player_name: string;
  raw_games: number;
  raw_score: number;
  raw_wins: number;
  leaderboard_games: number;
  leaderboard_score: number;
  leaderboard_wins: number;
  game_diff: number;
  score_diff: number;
  wins_diff: number;
  status: '✅ 一致' | '❌ 差异';
}

export class DataAuditService {
  async runFullAudit(
    dojoFilter: string = 'all',
    seasonFilter: string = 'all'
  ): Promise<{
    results: DataAuditResult[];
    summary: {
      totalPlayers: number;
      consistentPlayers: number;
      inconsistentPlayers: number;
      totalGameDiff: number;
      totalScoreDiff: number;
    };
  }> {
    const rawStats = await this.calculateRawStats(dojoFilter, seasonFilter);
    const leaderboardStats = await this.getLeaderboardStats();

    const results: DataAuditResult[] = [];
    const allPlayerNumbers = new Set([
      ...Object.keys(rawStats).map(Number),
      ...Object.keys(leaderboardStats).map(Number),
    ]);

    for (const playerNum of allPlayerNumbers) {
      const raw = rawStats[playerNum] || {
        total_games: 0,
        total_score: 0,
        wins: 0,
        player_name: `${playerNum}号`,
      };
      const lb = leaderboardStats[playerNum] || {
        total_games: 0,
        total_score: 0,
        wins: 0,
        player_name: raw.player_name,
      };

      const gameDiff = (lb.total_games || 0) - (raw.total_games || 0);
      const scoreDiff = (lb.total_score || 0) - (raw.total_score || 0);
      const winsDiff = (lb.wins || 0) - (raw.wins || 0);
      const hasDiff = Math.abs(gameDiff) > 0 || Math.abs(scoreDiff) > 0.01 || Math.abs(winsDiff) > 0;

      results.push({
        player_number: playerNum,
        player_name: raw.player_name || lb.player_name || `${playerNum}号`,
        raw_games: raw.total_games || 0,
        raw_score: Math.round((raw.total_score || 0) * 100) / 100,
        raw_wins: raw.wins || 0,
        leaderboard_games: lb.total_games || 0,
        leaderboard_score: Math.round((lb.total_score || 0) * 100) / 100,
        leaderboard_wins: lb.wins || 0,
        game_diff: gameDiff,
        score_diff: Math.round(scoreDiff * 100) / 100,
        wins_diff: winsDiff,
        status: hasDiff ? '❌ 差异' : '✅ 一致',
      });
    }

    results.sort((a, b) => Math.abs(b.score_diff) - Math.abs(a.score_diff));

    const inconsistentPlayers = results.filter(r => r.status === '❌ 差异');
    const summary = {
      totalPlayers: results.length,
      consistentPlayers: results.filter(r => r.status === '✅ 一致').length,
      inconsistentPlayers: inconsistentPlayers.length,
      totalGameDiff: inconsistentPlayers.reduce((sum, r) => sum + Math.abs(r.game_diff), 0),
      totalScoreDiff: inconsistentPlayers.reduce((sum, r) => sum + Math.abs(r.score_diff), 0),
    };

    return { results, summary };
  }

  private async calculateRawStats(dojoFilter: string, seasonFilter: string): Promise<Record<number, any>> {
    let query = supabase
      .from('player_round_records')
      .select('*, game_records!inner(*)')
      .eq('is_deleted', 'n')
      .eq('status', 'valid')
      .eq('game_records.is_deleted', 'n')
      .eq('game_records.status', 'valid');

    if (dojoFilter !== 'all') query = query.eq('game_records.dojo_id', Number(dojoFilter));
    if (seasonFilter !== 'all') query = query.eq('game_records.season_id', Number(seasonFilter));

    const { data: records, error } = await query;
    if (error) throw error;

    const uniqueRecordsMap = new Map<string, any>();
    for (const record of records || []) {
      const key = `${record.player_number}_${record.game_record_id}`;
      if (!uniqueRecordsMap.has(key)) {
        uniqueRecordsMap.set(key, record);
      }
    }

    const statsMap = aggregatePlayerStats(Array.from(uniqueRecordsMap.values()));
    const simplified: Record<number, any> = {};

    for (const stats of Object.values(statsMap)) {
      simplified[stats.player_number] = {
        player_number: stats.player_number,
        player_name: stats.player_name,
        total_games: stats.total_games,
        total_score: stats.total_score,
        wins: stats.wins,
      };
    }

    return simplified;
  }

  private async getLeaderboardStats(): Promise<Record<number, any>> {
    const { data: stats, error } = await supabase.from('player_stats').select('*');
    if (error) throw error;

    const statsMap: Record<number, any> = {};
    for (const stat of stats || []) {
      statsMap[stat.player_number] = {
        player_number: stat.player_number,
        player_name: stat.player_name || `${stat.player_number}号`,
        total_games: stat.total_games || 0,
        total_score: stat.total_score || 0,
        wins: stat.wins || 0,
      };
    }

    return statsMap;
  }

  generateAuditReport(results: DataAuditResult[], summary: any): string {
    const lines: string[] = [];
    lines.push('====================');
    lines.push('[V106 数据核对报告]');
    lines.push('====================');
    lines.push('');
    lines.push('汇总信息:');
    lines.push(`  总玩家数: ${summary.totalPlayers}`);
    lines.push(`  数据一致: ${summary.consistentPlayers} 位`);
    lines.push(`  数据差异: ${summary.inconsistentPlayers} 位`);
    lines.push(`  总局数差异: ${summary.totalGameDiff}`);
    lines.push(`  总积分差异: ${summary.totalScoreDiff}`);
    lines.push('');

    const inconsistent = results.filter(r => r.status === '❌ 差异');
    if (inconsistent.length > 0) {
      lines.push('差异详情:');
      lines.push('编号 | 昵称 | 原始局数 | 榜单局数 | 局数差 | 原始分 | 榜单分 | 分差');
      for (const r of inconsistent) {
        lines.push(
          `${String(r.player_number).padStart(4)} | ` +
          `${r.player_name.padEnd(6)} | ` +
          `${String(r.raw_games).padStart(4)} | ` +
          `${String(r.leaderboard_games).padStart(4)} | ` +
          `${r.game_diff >= 0 ? '+' : ''}${String(r.game_diff).padStart(3)} | ` +
          `${String(r.raw_score).padStart(6)} | ` +
          `${String(r.leaderboard_score).padStart(6)} | ` +
          `${r.score_diff >= 0 ? '+' : ''}${String(r.score_diff).padStart(6)}`
        );
      }
    } else {
      lines.push('所有玩家数据一致。');
    }

    return lines.join('\n');
  }
}

export const dataAuditService = new DataAuditService();
